'use client';

import { cn } from '@/core/utils/cn';
import { Shield, Clock, Lock, CheckCircle, Server, KeyRound } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { usePageContent } from '@/core/i18n/content';
import { TRUST_CONTENT } from '@/core/i18n/content/trust.content';
import { useTranslation } from '@/core/i18n';

/* -------------------------------------------------------------------------- */
/*  Icon map for trust badges                                                 */
/* -------------------------------------------------------------------------- */

const BADGE_ICONS: LucideIcon[] = [Shield, Lock, CheckCircle, Server, Clock, KeyRound];

/* -------------------------------------------------------------------------- */
/*  TrustBadgesSection                                                        */
/* -------------------------------------------------------------------------- */

export function TrustBadgesSection() {
  const { t } = useTranslation();
  const c = usePageContent(TRUST_CONTENT);
  return (
    <section
      id="for-hotels-trust"
      className="relative w-full overflow-hidden bg-hms-surface py-20 sm:py-28"
    >
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            'radial-gradient(ellipse 60% 50% at 50% 40%, rgba(200,145,58,0.04) 0%, transparent 70%)',
        }}
      />
      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mx-auto max-w-2xl text-center">
          <p
            id="for-hotels-trust-badge-label"
            className="mb-3 text-xs font-semibold uppercase tracking-widest text-hms-gold"
          >
            {c.badge}
          </p>
          <h2
            id="for-hotels-trust-title"
            className={cn(
              'text-3xl font-extrabold tracking-tight sm:text-4xl',
              'bg-gradient-to-r from-hms-gold via-hms-goldLt to-amber-400',
              'bg-clip-text text-transparent',
            )}
          >
            {c.title}
          </h2>
          <p
            id="for-hotels-trust-subtitle"
            className="mt-4 text-base text-hms-textSub sm:text-lg"
          >
            {c.subtitle}
          </p>
        </div>

        {/* Customer Logos Grid */}
        <div
          id="for-hotels-customer-logos"
          className="mt-14 grid grid-cols-2 gap-4 sm:grid-cols-4"
        >
          {c.logos.map((logo, idx) => (
            <div
              key={logo.initials}
              id={`for-hotels-customer-logo-${idx}`}
              className={cn(
                'flex items-center gap-3 rounded-xl border border-hms-border bg-hms-card p-4',
                'transition-colors hover:border-hms-gold/20',
              )}
            >
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-hms-gold/10">
                <span className="text-xs font-bold text-hms-gold">{logo.initials}</span>
              </div>
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold text-hms-text">{logo.name}</p>
                <p className="text-xs text-hms-textDim">{logo.location}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Trust Badges Row */}
        <div
          id="for-hotels-trust-badges"
          className="mt-12 flex flex-wrap items-center justify-center gap-4 sm:gap-6"
        >
          {c.badges.map((badge, idx) => {
            const Icon = BADGE_ICONS[idx] ?? Shield;
            return (
              <div
                key={idx}
                id={`for-hotels-trust-badge-${idx}`}
                className={cn(
                  'flex items-center gap-2.5 rounded-lg border border-hms-border/50 bg-hms-card/50 px-4 py-2.5',
                  'transition-colors hover:border-hms-gold/20',
                )}
              >
                <Icon className="h-4 w-4 text-hms-green" />
                <div>
                  <p className="text-xs font-semibold text-hms-text">{badge.label}</p>
                  <p className="text-[10px] text-hms-textDim">{badge.description}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
